import React, { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Settings, Upload, Grid, List, Wand2, Edit2, Star, Heart, MessageCircle, Sparkles, Grid3x3, ArrowUpDown, Share2, User, LayoutGrid, Download, Loader2, Film } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { useProjectStore } from '../stores/projectStore'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { Image, Video } from '../types'
import JSZip from 'jszip'
import Header from '../components/layout/Header'
import ScenesList from '../components/project/ScenesList'
import ImageGallery from '../components/project/ImageGallery'
import VideoGallery from '../components/project/VideoGallery'
import StoryEditor from '../components/project/StoryEditor'
import CreateSceneDialog from '../components/project/CreateSceneDialog'
import ProjectSettingsDialog from '../components/project/ProjectSettingsDialog'
import ImageUploadDropzone from '../components/project/ImageUploadDropzone'
import VideoUploadDropzone from '../components/project/VideoUploadDropzone'
import ImageGenerationDialog from '../components/project/ImageGenerationDialog'
import VideoGenerationDialog from '../components/project/VideoGenerationDialog'
import ImageLightbox from '../components/project/ImageLightbox'
import VideoLightbox from '../components/project/VideoLightbox'
import DragDropZone from '../components/project/DragDropZone'
import ScenesOverviewDialog from '../components/project/ScenesOverviewDialog'

const ProjectView: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { 
    currentProject, 
    scenes, 
    images,
    videos,
    currentModule,
    loading, 
    fetchScenes, 
    fetchImages,
    fetchVideos,
    fetchProjectById,
    setCurrentProject,
    updateProject,
    updateScene
  } = useProjectStore()

  const [selectedSceneId, setSelectedSceneId] = useState<string | null>(null)
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null)
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null)
  const [lightboxShowComments, setLightboxShowComments] = useState(false)
  const [showCreateScene, setShowCreateScene] = useState(false)
  const [showProjectSettings, setShowProjectSettings] = useState(false)
  const [settingsInitialTab, setSettingsInitialTab] = useState<'basic' | 'document' | 'sharing' | 'characters'>('basic')
  const [showImageUpload, setShowImageUpload] = useState(false)
  const [showVideoUpload, setShowVideoUpload] = useState(false)
  const [showImageGeneration, setShowImageGeneration] = useState(false)
  const [showVideoGeneration, setShowVideoGeneration] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [isEditingProjectName, setIsEditingProjectName] = useState(false)
  const [editedProjectName, setEditedProjectName] = useState('')
  const [isEditingSceneName, setIsEditingSceneName] = useState(false)
  const [editedSceneName, setEditedSceneName] = useState('')
  const [filterType, setFilterType] = useState<'all' | 'main' | 'favorites' | 'generated' | 'uploaded' | 'comments'>('all')
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc')
  const [thumbnailScale, setThumbnailScale] = useState(1.5) // Default to 75% of max (0.5 to 3.0 range, 1.5 is ~75%)
  const [sceneThumbnailScale, setSceneThumbnailScale] = useState(1.0) // Side panel thumbnail scale (1.0 = full width)
  const [showScenesOverview, setShowScenesOverview] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  // Export favorited images from all scenes as a zip file
  const handleExportFavorites = useCallback(async () => {
    if (!projectId || !currentProject) return
    
    setIsExporting(true)
    
    try {
      // Fetch all images from all scenes in this project that are favorites
      const imagesQuery = query(
        collection(db, 'images'),
        where('projectId', '==', projectId),
        where('isFavorite', '==', true)
      )
      const snapshot = await getDocs(imagesQuery)
      
      if (snapshot.empty) {
        alert('No favorited images found to export.')
        setIsExporting(false)
        return
      }

      const favoriteImages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Image[]

      // Group images by scene
      const imagesByScene = new Map<string, Image[]>()
      favoriteImages.forEach(img => {
        const sceneImages = imagesByScene.get(img.sceneId) || []
        sceneImages.push(img)
        imagesByScene.set(img.sceneId, sceneImages)
      })

      // Create zip file
      const zip = new JSZip()
      const sanitizeFilename = (str: string) => str.replace(/[^a-z0-9]/gi, '_').toLowerCase()
      const projectName = sanitizeFilename(currentProject.title)
      
      let imageNumber = 1
      const totalImages = favoriteImages.length

      for (const [sceneId, sceneImages] of imagesByScene) {
        const scene = scenes.find(s => s.id === sceneId)
        const sceneName = scene ? sanitizeFilename(scene.title) : `scene_${sceneId.slice(0, 6)}`
        
        for (const img of sceneImages) {
          try {
            // Fetch the image
            const response = await fetch(img.url, { mode: 'cors' })
            if (!response.ok) continue
            
            const blob = await response.blob()
            
            // Determine file extension
            let extension = 'jpg'
            if (blob.type) {
              extension = blob.type.split('/')[1] || 'jpg'
            } else if (img.url.includes('.')) {
              const urlParts = img.url.split('.')
              const lastPart = urlParts[urlParts.length - 1].split('?')[0]
              if (lastPart.match(/^(jpg|jpeg|png|gif|webp)$/i)) {
                extension = lastPart.toLowerCase()
              }
            }
            
            // Create filename: projectname_scenename_001.jpg
            const filename = `${projectName}_${sceneName}_${String(imageNumber).padStart(3, '0')}.${extension}`
            
            zip.file(filename, blob)
            imageNumber++
          } catch (err) {
            console.error(`Failed to fetch image ${img.id}:`, err)
          }
        }
      }

      // Generate and download the zip
      const content = await zip.generateAsync({ type: 'blob' })
      const url = URL.createObjectURL(content)
      const link = document.createElement('a')
      link.href = url
      link.download = `${projectName}_favorites_${new Date().toISOString().slice(0, 10)}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      
      alert(`Successfully exported ${totalImages} favorited images!`)
    } catch (error) {
      console.error('Export failed:', error)
      alert('Failed to export images. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }, [projectId, currentProject, scenes])

  useEffect(() => {
    if (!projectId || !user) return

    const loadProject = async () => {
      // Load project data - first check store, then fetch from Firestore if needed
      const project = useProjectStore.getState().projects.find(p => p.id === projectId)
      if (project) {
        setCurrentProject(project)
        fetchScenes(projectId)
      } else {
        // If project not found in store, fetch it from Firestore
        const fetchedProject = await fetchProjectById(projectId)
        if (fetchedProject) {
          fetchScenes(projectId)
        } else {
          // Project doesn't exist, redirect to dashboard
          navigate('/dashboard')
        }
      }
    }

    loadProject()
  }, [projectId, user, navigate, setCurrentProject, fetchScenes, fetchProjectById])

  useEffect(() => {
    // Load images or videos for the selected scene based on current module
    if (selectedSceneId) {
      if (currentModule === 'images') {
        fetchImages(selectedSceneId)
      } else if (currentModule === 'video') {
        fetchVideos(selectedSceneId)
      }
    }
  }, [selectedSceneId, currentModule, fetchImages, fetchVideos])

  useEffect(() => {
    // Auto-select first scene when scenes load
    if (scenes.length > 0 && !selectedSceneId) {
      setSelectedSceneId(scenes[0].id)
    }
  }, [scenes, selectedSceneId])

  const handleSceneSelect = (sceneId: string) => {
    setSelectedSceneId(sceneId)
    setSelectedImageId(null) // Clear image selection when switching scenes
  }

  const handleImageSelect = (imageId: string, showComments: boolean = false) => {
    setSelectedImageId(imageId)
    setLightboxShowComments(showComments)
  }

  const handleVideoSelect = (videoId: string, showComments: boolean = false) => {
    setSelectedVideoId(videoId)
    setLightboxShowComments(showComments)
  }

  const handleBackToDashboard = () => {
    navigate('/dashboard')
  }

  const handleEditProjectName = () => {
    if (currentProject) {
      setEditedProjectName(currentProject.title)
      setIsEditingProjectName(true)
    }
  }

  const handleSaveProjectName = async () => {
    if (!currentProject || !editedProjectName.trim()) {
      setIsEditingProjectName(false)
      return
    }
    try {
      await updateProject(currentProject.id, { title: editedProjectName.trim() })
      setIsEditingProjectName(false)
    } catch (error) {
      console.error('Failed to update project name:', error)
    }
  }

  const handleProjectNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveProjectName()
    } else if (e.key === 'Escape') {
      setIsEditingProjectName(false)
    }
  }

  const handleEditSceneName = () => {
    if (selectedScene) {
      setEditedSceneName(selectedScene.title)
      setIsEditingSceneName(true)
    }
  }

  const handleSaveSceneName = async () => {
    if (!selectedScene || !editedSceneName.trim()) {
      setIsEditingSceneName(false)
      return
    }
    try {
      await updateScene(selectedScene.id, { title: editedSceneName.trim() })
      setIsEditingSceneName(false)
    } catch (error) {
      console.error('Failed to update scene name:', error)
    }
  }

  const handleSceneNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveSceneName()
    } else if (e.key === 'Escape') {
      setIsEditingSceneName(false)
    }
  }

  const selectedScene = scenes.find(s => s.id === selectedSceneId)
  const selectedImage = images.find(img => img.id === selectedImageId)
  const sceneImages = images.filter(img => img.sceneId === selectedSceneId)

  if (loading && !currentProject) {
    return (
      <div className="min-h-screen bg-primary-700 flex items-center justify-center">
        <div className="glass-card p-8 text-center">
          <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white font-body font-light">Loading project...</p>
        </div>
      </div>
    )
  }

  if (!currentProject) {
    return (
      <div className="min-h-screen bg-primary-700 flex items-center justify-center">
        <div className="glass-card p-8 text-center">
          <p className="text-white font-body font-light mb-4">Project not found</p>
          <button
            onClick={handleBackToDashboard}
            className="glass-button bg-primary-500/30 hover:bg-primary-500/40 text-white px-6 py-2 font-ui font-normal"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-primary-700">
      <div className="flex flex-col h-screen">
        <Header showModuleTabs={true} />
        
        {/* Project Header */}
        <div className="border-b border-glass-border bg-primary-800/50 backdrop-blur-sm">
          <div className="w-full px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleBackToDashboard}
                  className="glass-button p-2 hover:bg-glass-medium"
                >
                  <ArrowLeft className="w-5 h-5 text-white" />
                </button>
                
                <div className="flex items-center space-x-3">
                  {/* Project Name */}
                  <div className="flex items-center space-x-2">
                    {isEditingProjectName ? (
                      <input
                        type="text"
                        value={editedProjectName}
                        onChange={(e) => setEditedProjectName(e.target.value)}
                        onBlur={handleSaveProjectName}
                        onKeyDown={handleProjectNameKeyDown}
                        autoFocus
                        className="glass-input text-xl font-headline text-white bg-white/10 px-2 py-1 rounded"
                      />
                    ) : (
                      <h1 className="text-xl font-headline text-white">{currentProject.title}</h1>
                    )}
                    <button
                      onClick={handleEditProjectName}
                      className="glass-button p-1 hover:bg-white/10 text-white/50 hover:text-white"
                      title="Edit project name"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <span className="text-white/50 text-sm font-body">
                      ({scenes.length} {scenes.length === 1 ? 'scene' : 'scenes'})
                    </span>
                  </div>

                  {/* Separator & Scene Name */}
                  {selectedScene && (
                    <>
                      <span className="text-white/30 text-xl font-headline">/</span>
                      <div className="flex items-center space-x-2">
                        {isEditingSceneName ? (
                          <input
                            type="text"
                            value={editedSceneName}
                            onChange={(e) => setEditedSceneName(e.target.value)}
                            onBlur={handleSaveSceneName}
                            onKeyDown={handleSceneNameKeyDown}
                            autoFocus
                            className="glass-input text-xl font-headline text-white bg-white/10 px-2 py-1 rounded"
                          />
                        ) : (
                          <h2 className="text-xl font-headline text-white">{selectedScene.title}</h2>
                        )}
                        <button
                          onClick={handleEditSceneName}
                          className="glass-button p-1 hover:bg-white/10 text-white/50 hover:text-white"
                          title="Edit scene name"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <span className="text-white/50 text-sm font-body">
                          ({sceneImages.length} {sceneImages.length === 1 ? 'image' : 'images'})
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-3">
                {/* Module-specific controls - Show only for Images and Video modules */}
                {(currentModule === 'images' || currentModule === 'video') && (
                  <>
                    {/* Thumbnail Size Slider */}
                    <div className="glass-card p-1 flex items-center space-x-2 px-3 h-9" title="Thumbnail Size">
                      <span className="text-white/40 text-xs">S</span>
                      <input
                        type="range"
                        min="0.5"
                        max="3.0"
                        step="0.1"
                        value={thumbnailScale}
                        onChange={(e) => setThumbnailScale(parseFloat(e.target.value))}
                        className={`w-40 ${currentModule === 'video' ? 'accent-cyan-500' : 'accent-purple-500'}`}
                      />
                      <span className="text-white/40 text-xs">L</span>
                    </div>

                    {/* View Mode Toggle */}
                    <div className="glass-card p-1 flex">
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`p-2 rounded transition-colors ${
                          viewMode === 'grid' 
                            ? 'bg-glass-medium text-white' 
                            : 'text-white/60 hover:text-white'
                        }`}
                        title="Grid View"
                      >
                        <Grid className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setViewMode('list')}
                        className={`p-2 rounded transition-colors ${
                          viewMode === 'list' 
                            ? 'bg-glass-medium text-white' 
                            : 'text-white/60 hover:text-white'
                        }`}
                        title="List View"
                      >
                        <List className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Filter Icons */}
                    <div className="glass-card p-1 flex">
                      <button
                        onClick={() => setFilterType('all')}
                        className={`p-2 rounded transition-colors ${
                          filterType === 'all' 
                            ? 'bg-glass-medium text-white' 
                            : 'text-white/60 hover:text-white'
                        }`}
                        title={currentModule === 'video' ? 'All Videos' : 'All Images'}
                      >
                        <Grid3x3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setFilterType(prev => prev === 'main' ? 'all' : 'main')}
                        className={`p-2 rounded transition-colors ${
                          filterType === 'main' 
                            ? 'bg-glass-medium text-yellow-300' 
                            : 'text-white/60 hover:text-yellow-400'
                        }`}
                        title={currentModule === 'video' ? 'Main Videos' : 'Main Images'}
                      >
                        <Star className="w-4 h-4" fill={filterType === 'main' ? 'currentColor' : 'none'} />
                      </button>
                      <button
                        onClick={() => setFilterType(prev => prev === 'favorites' ? 'all' : 'favorites')}
                        className={`p-2 rounded transition-colors ${
                          filterType === 'favorites' 
                            ? 'bg-glass-medium text-red-300' 
                            : 'text-white/60 hover:text-red-400'
                        }`}
                        title="Favorites"
                      >
                        <Heart className="w-4 h-4" fill={filterType === 'favorites' ? 'currentColor' : 'none'} />
                      </button>
                      <button
                        onClick={() => setFilterType(prev => prev === 'generated' ? 'all' : 'generated')}
                        className={`p-2 rounded transition-colors ${
                          filterType === 'generated' 
                            ? 'bg-glass-medium text-purple-300' 
                            : 'text-white/60 hover:text-purple-400'
                        }`}
                        title="AI Generated"
                      >
                        <Sparkles className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setFilterType(prev => prev === 'uploaded' ? 'all' : 'uploaded')}
                        className={`p-2 rounded transition-colors ${
                          filterType === 'uploaded' 
                            ? 'bg-glass-medium text-green-300' 
                            : 'text-white/60 hover:text-green-400'
                        }`}
                        title="Uploaded"
                      >
                        <Upload className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setFilterType(prev => prev === 'comments' ? 'all' : 'comments')}
                        className={`p-2 rounded transition-colors ${
                          filterType === 'comments' 
                            ? 'bg-glass-medium text-blue-300' 
                            : 'text-white/60 hover:text-blue-400'
                        }`}
                        title="Top Comments"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Sort Button */}
                    <div className="glass-card p-1">
                      <button
                        onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                        className="p-2 rounded hover:bg-glass-medium text-white/60 hover:text-white transition-colors"
                        title={`Sort by date ${sortOrder === 'desc' ? 'descending' : 'ascending'}`}
                      >
                        <ArrowUpDown className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Upload/Generate buttons based on module */}
                    {currentModule === 'images' && (
                      <>
                        <button
                          onClick={() => setShowImageUpload(true)}
                          className="glass-button bg-green-500/30 hover:bg-green-500/40 text-white px-4 py-2 flex items-center space-x-2 font-ui font-normal"
                          disabled={!selectedSceneId}
                        >
                          <Upload className="w-4 h-4" />
                          <span>Upload</span>
                        </button>

                        <button
                          onClick={() => setShowImageGeneration(true)}
                          className="glass-button bg-purple-500/30 hover:bg-purple-500/40 text-white px-4 py-2 flex items-center space-x-2 font-ui font-normal"
                          disabled={!selectedSceneId}
                        >
                          <Wand2 className="w-4 h-4" />
                          <span>Generate</span>
                        </button>
                      </>
                    )}

                    {currentModule === 'video' && (
                      <>
                        <button
                          onClick={() => setShowVideoUpload(true)}
                          className="glass-button bg-cyan-500/30 hover:bg-cyan-500/40 text-white px-4 py-2 flex items-center space-x-2 font-ui font-normal"
                          disabled={!selectedSceneId}
                        >
                          <Film className="w-4 h-4" />
                          <span>Upload Video</span>
                        </button>

                        <button
                          onClick={() => setShowVideoGeneration(true)}
                          className="glass-button bg-purple-500/30 hover:bg-purple-500/40 text-white px-4 py-2 flex items-center space-x-2 font-ui font-normal"
                          disabled={!selectedSceneId}
                        >
                          <Wand2 className="w-4 h-4" />
                          <span>Generate</span>
                        </button>
                      </>
                    )}
                  </>
                )}

                <button
                  onClick={() => {
                    setSettingsInitialTab('characters')
                    setShowProjectSettings(true)
                  }}
                  className="glass-button bg-gradient-to-r from-pink-500/30 to-purple-500/30 hover:from-pink-500/40 hover:to-purple-500/40 text-white px-4 py-2 flex items-center space-x-2 font-ui font-normal"
                >
                  <User className="w-4 h-4" />
                  <span>Characters</span>
                </button>

                <button
                  onClick={() => {
                    setSettingsInitialTab('sharing')
                    setShowProjectSettings(true)
                  }}
                  className="glass-button bg-blue-500/30 hover:bg-blue-500/40 text-white px-4 py-2 flex items-center space-x-2 font-ui font-normal"
                >
                  <Share2 className="w-4 h-4" />
                  <span>Share</span>
                </button>

                <button
                  onClick={() => setShowScenesOverview(true)}
                  className="glass-button bg-amber-500/30 hover:bg-amber-500/40 text-white px-4 py-2 flex items-center space-x-2 font-ui font-normal"
                >
                  <LayoutGrid className="w-4 h-4" />
                  <span>Overview</span>
                </button>

                {currentModule === 'images' && (
                  <button
                    onClick={handleExportFavorites}
                    disabled={isExporting}
                    className="glass-button bg-green-500/30 hover:bg-green-500/40 text-white px-4 py-2 flex items-center space-x-2 font-ui font-normal disabled:opacity-50"
                    title="Export all favorited images as ZIP"
                  >
                    {isExporting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    <span>{isExporting ? 'Exporting...' : 'Export'}</span>
                  </button>
                )}

                <button
                  onClick={() => {
                    setSettingsInitialTab('basic')
                    setShowProjectSettings(true)
                  }}
                  className="glass-button p-2 hover:bg-glass-medium"
                >
                  <Settings className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar - Scenes */}
          <div className="w-80 border-r border-glass-border bg-primary-800/30 backdrop-blur-sm flex flex-col">
            {/* Scenes List */}
            <div className="flex-1 overflow-hidden">
              <ScenesList
                scenes={scenes}
                selectedSceneId={selectedSceneId}
                onSceneSelect={handleSceneSelect}
                onCreateScene={() => setShowCreateScene(true)}
                projectId={projectId!}
                projectAspectRatio={currentProject.aspectRatio}
                thumbnailScale={sceneThumbnailScale}
                onThumbnailScaleChange={setSceneThumbnailScale}
              />
            </div>
          </div>

          {/* Right Main Area - Module Content */}
          <div className="flex-1 overflow-hidden">
            {currentModule === 'story' && (
              <StoryEditor scene={selectedScene} />
            )}
            
            {currentModule === 'images' && (
              <DragDropZone sceneId={selectedSceneId} projectId={projectId!}>
                <ImageGallery
                  images={images}
                  selectedImageId={selectedImageId}
                  selectedScene={selectedScene}
                  viewMode={viewMode}
                  filterType={filterType}
                  sortOrder={sortOrder}
                  thumbnailScale={thumbnailScale}
                  onImageSelect={handleImageSelect}
                  onUploadClick={() => setShowImageUpload(true)}
                  onGenerateClick={() => setShowImageGeneration(true)}
                />
              </DragDropZone>
            )}
            
            {currentModule === 'video' && (
              <VideoGallery
                videos={videos}
                selectedVideoId={selectedVideoId}
                selectedScene={selectedScene}
                viewMode={viewMode}
                filterType={filterType}
                sortOrder={sortOrder}
                thumbnailScale={thumbnailScale}
                onVideoSelect={handleVideoSelect}
                onUploadClick={() => setShowVideoUpload(true)}
                onGenerateClick={() => setShowVideoGeneration(true)}
              />
            )}
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <CreateSceneDialog
        open={showCreateScene}
        onClose={() => setShowCreateScene(false)}
        projectId={projectId!}
      />

      <ProjectSettingsDialog
        open={showProjectSettings}
        onClose={async () => {
          setShowProjectSettings(false)
          // Refresh project data from Firestore to ensure we have the latest
          if (projectId) {
            await fetchProjectById(projectId)
          }
        }}
        project={currentProject}
        initialTab={settingsInitialTab}
      />

      <ImageUploadDropzone
        open={showImageUpload}
        onClose={() => setShowImageUpload(false)}
        sceneId={selectedSceneId}
        projectId={projectId!}
      />

      <ImageGenerationDialog
        open={showImageGeneration}
        onClose={() => setShowImageGeneration(false)}
        sceneId={selectedSceneId}
        projectId={projectId!}
      />

      <ImageLightbox
        open={!!selectedImageId}
        onClose={() => {
          setSelectedImageId(null)
          setLightboxShowComments(false)
        }}
        image={selectedImage}
        images={images}
        onImageSelect={handleImageSelect}
        initialShowComments={lightboxShowComments}
        projectTitle={currentProject.title}
        sceneTitle={selectedScene?.title}
      />

      <ScenesOverviewDialog
        open={showScenesOverview}
        onClose={() => setShowScenesOverview(false)}
        scenes={scenes}
        projectId={projectId!}
        projectAspectRatio={currentProject.aspectRatio}
        onSceneSelect={(sceneId) => {
          handleSceneSelect(sceneId)
          setShowScenesOverview(false)
        }}
        onExport={handleExportFavorites}
      />

      <VideoUploadDropzone
        open={showVideoUpload}
        onClose={() => setShowVideoUpload(false)}
        sceneId={selectedSceneId}
        projectId={projectId!}
      />

      <VideoLightbox
        open={!!selectedVideoId}
        onClose={() => {
          setSelectedVideoId(null)
          setLightboxShowComments(false)
        }}
        video={videos.find(v => v.id === selectedVideoId)}
        videos={videos}
        onVideoSelect={handleVideoSelect}
        initialShowComments={lightboxShowComments}
        projectTitle={currentProject.title}
        sceneTitle={selectedScene?.title}
      />

      <VideoGenerationDialog
        open={showVideoGeneration}
        onClose={() => setShowVideoGeneration(false)}
        sceneId={selectedSceneId}
        projectId={projectId!}
      />
    </div>
  )
}

export default ProjectView 